/**
 * Types and DOM layout helpers for the Three.js addon inspector FPS toggle (`#profiler-toggle`).
 */
import { Style } from 'three/addons/inspector/ui/Style.js';

import { parseJson } from '../../utils/Utils.js';

import type { InspectorBase } from 'three/webgpu';

const PARAMETERS_GROUP_TITLE_CLASS = 'gnsx-parameters-group-title';

/** Three.js inspector `createParameters()` group — only the DOM handles we need for floating UI. */
export interface InspectorParametersGroupLike {
  name?: string;
  paramList: {
    domElement: HTMLElement;
  };
}

function getParametersGroupHeaderCell(group: InspectorParametersGroupLike): HTMLElement | null {
  const panel = group.paramList.domElement;
  const row = panel.firstElementChild;
  if (row instanceof HTMLElement && row.classList.contains('list-item-row')) {
    return row.querySelector<HTMLElement>('.list-item-cell:first-child');
  }
  return panel.querySelector<HTMLElement>('.list-item-row > .list-item-cell:first-child');
}

/** Updates the visible Parameters-tab group title (assigning `.name` alone does not refresh the DOM). */
export function setInspectorParametersGroupName(
  group: InspectorParametersGroupLike & { __gnsxTitleLabel?: HTMLElement },
  title: string,
): void {
  group.name = title;

  let label = group.__gnsxTitleLabel;
  if (!label?.isConnected) {
    const cell = getParametersGroupHeaderCell(group);
    if (!cell) {
      return;
    }
    label = cell.querySelector<HTMLElement>(`.${PARAMETERS_GROUP_TITLE_CLASS}`) ?? undefined;
    if (!label) {
      for (const child of [...cell.childNodes]) {
        if (child.nodeType === Node.TEXT_NODE) {
          child.remove();
        }
      }
      label = document.createElement('span');
      label.className = PARAMETERS_GROUP_TITLE_CLASS;
      cell.appendChild(label);
    }
    group.__gnsxTitleLabel = label;
  }

  label.textContent = title;
}

const DEFAULT_PROFILER_PANEL_STORAGE_KEY = 'gnsx-profiler-panel-position';

interface SavedPanelPosition {
  x: number;
  y: number;
}

function loadPanelPosition(storageKey: string): SavedPanelPosition | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = parseJson(raw) as SavedPanelPosition;
    if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') return null;
    if (!Number.isFinite(parsed.x) || !Number.isFinite(parsed.y)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function savePanelPosition(storageKey: string, position: SavedPanelPosition): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(position));
  } catch {
    // ignore quota / private mode
  }
}

function clampPanelPosition(panel: HTMLElement, x: number, y: number): SavedPanelPosition {
  const w = panel.offsetWidth || panel.getBoundingClientRect().width || 280;
  const h = panel.offsetHeight || panel.getBoundingClientRect().height || 120;
  const maxX = Math.max(0, window.innerWidth - w);
  const maxY = Math.max(0, window.innerHeight - h);
  return {
    x: Math.min(maxX, Math.max(0, x)),
    y: Math.min(maxY, Math.max(0, y)),
  };
}

function readPanelPosition(panel: HTMLElement): SavedPanelPosition {
  const rect = panel.getBoundingClientRect();
  return { x: rect.left, y: rect.top };
}

function applyPanelPosition(panel: HTMLElement, x: number, y: number): void {
  const clamped = clampPanelPosition(panel, x, y);
  panel.style.left = `${clamped.x}px`;
  panel.style.top = `${clamped.y}px`;
}

/**
 * Detaches a Parameters-tab group as a fixed, draggable overlay and persists its screen position.
 *
 * @returns Disposer — call when tearing down the game loop / inspector.
 */
export function attachFloatingDraggableParametersGroup(
  host: HTMLElement,
  group: InspectorParametersGroupLike,
  options?: { storageKey?: string },
): () => void {
  Style.init();

  const storageKey = options?.storageKey ?? DEFAULT_PROFILER_PANEL_STORAGE_KEY;
  const panel = group.paramList.domElement;
  panel.classList.add('parameters');
  const header = panel.querySelector<HTMLElement>(':scope > .list-item-row');
  if (!header) {
    console.warn('[Profiler] Could not find parameters group header for drag handle');
    return () => {
      panel.remove();
    };
  }

  panel.classList.add('gnsx-floating-parameters-group');
  header.classList.add('gnsx-floating-parameters-group-header');
  panel.style.position = 'fixed';
  panel.style.zIndex = '10000';
  panel.style.margin = '0';
  panel.style.pointerEvents = 'auto';
  panel.style.boxShadow = '0 4px 24px rgba(0, 0, 0, 0.45)';
  panel.style.backgroundColor = '#1e1e24';
  panel.style.border = '1px solid #4a4a5a';
  panel.style.borderRadius = '6px';
  panel.style.overflow = 'hidden';
  header.style.backgroundColor = '#243040';
  header.style.cursor = 'grab';
  header.style.userSelect = 'none';

  const childrenContainer = panel.querySelector<HTMLElement>('.list-children-container');
  if (childrenContainer) {
    childrenContainer.style.backgroundColor = '#1e1e24';
    for (const row of childrenContainer.querySelectorAll<HTMLElement>('.list-item-row')) {
      row.style.backgroundColor = '#252530';
    }
  }

  host.appendChild(panel);

  const placeInitial = (): void => {
    const saved = loadPanelPosition(storageKey);
    if (saved) {
      applyPanelPosition(panel, saved.x, saved.y);
      return;
    }
    const margin = 16;
    const w = panel.getBoundingClientRect().width || panel.offsetWidth || 280;
    const x = window.innerWidth - w - margin;
    applyPanelPosition(panel, x, margin);
  };

  requestAnimationFrame(placeInitial);

  let dragPointerId: number | null = null;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragOriginX = 0;
  let dragOriginY = 0;
  let isDragging = false;
  let suppressHeaderClick = false;
  const dragThresholdPx = 4;

  const onHeaderPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button, a, input, label')) return;

    dragPointerId = event.pointerId;
    dragStartX = event.clientX;
    dragStartY = event.clientY;
    const origin = readPanelPosition(panel);
    dragOriginX = origin.x;
    dragOriginY = origin.y;
    isDragging = false;
    suppressHeaderClick = false;
    header.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const onHeaderPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== dragPointerId) return;

    const dx = event.clientX - dragStartX;
    const dy = event.clientY - dragStartY;
    if (!isDragging) {
      if (Math.hypot(dx, dy) < dragThresholdPx) return;
      isDragging = true;
      suppressHeaderClick = true;
      header.style.cursor = 'grabbing';
    }

    applyPanelPosition(panel, dragOriginX + dx, dragOriginY + dy);
  };

  const finishDrag = (event: PointerEvent): void => {
    if (event.pointerId !== dragPointerId) return;

    if (header.hasPointerCapture(event.pointerId)) {
      header.releasePointerCapture(event.pointerId);
    }
    dragPointerId = null;
    header.style.cursor = 'grab';

    if (isDragging) {
      const pos = readPanelPosition(panel);
      savePanelPosition(storageKey, clampPanelPosition(panel, pos.x, pos.y));
    }
    isDragging = false;
  };

  const onHeaderClickCapture = (event: MouseEvent): void => {
    if (!suppressHeaderClick) return;
    event.preventDefault();
    event.stopPropagation();
    suppressHeaderClick = false;
  };

  const onWindowResize = (): void => {
    const { x, y } = readPanelPosition(panel);
    const clamped = clampPanelPosition(panel, x, y);
    applyPanelPosition(panel, clamped.x, clamped.y);
    savePanelPosition(storageKey, clamped);
  };

  header.addEventListener('pointerdown', onHeaderPointerDown);
  header.addEventListener('pointermove', onHeaderPointerMove);
  header.addEventListener('pointerup', finishDrag);
  header.addEventListener('pointercancel', finishDrag);
  header.addEventListener('click', onHeaderClickCapture, true);
  window.addEventListener('resize', onWindowResize);

  return () => {
    header.removeEventListener('pointerdown', onHeaderPointerDown);
    header.removeEventListener('pointermove', onHeaderPointerMove);
    header.removeEventListener('pointerup', finishDrag);
    header.removeEventListener('pointercancel', finishDrag);
    header.removeEventListener('click', onHeaderClickCapture, true);
    window.removeEventListener('resize', onWindowResize);
    panel.remove();
  };
}

/** True when Three.js exposes `inspector` on the native renderer (e.g. `WebGPURenderer`, including WebGL fallback). */
export function nativeRendererHasInspectorSlot(native: unknown): native is { inspector: InspectorBase } {
  return typeof native === 'object' && native !== null && 'inspector' in native;
}

/** Placement for the Three.js inspector FPS toggle within the game host element. */
export type InspectorTogglePosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'center-top'
  | 'center-bottom';

/**
 * Pixel inset from the container edge (or nudge) for the inspector FPS toggle.
 *
 * @remarks
 * For `center-top` / `center-bottom`, `x` is a horizontal nudge from the visual centre (via `calc(50% + x)`).
 * For top row positions, `y` is inset from the top; for bottom row positions, `y` is inset from the bottom.
 */
export interface InspectorToggleOffset {
  x: number;
  y: number;
}

/** Positions and scales the Three.js inspector `#profiler-toggle` inside the inspector shell element. */
export function applyThreeInspectorTogglePositioning(
  shell: HTMLElement,
  inspector: {
    position: InspectorTogglePosition;
    offset: InspectorToggleOffset;
    /** Uniform scale for the FPS toggle (CSS `transform: ... scale(...)`). */
    scale: number;
  },
): void {
  const { position, offset: { x, y }, scale } = inspector;

  const toggle = shell.querySelector<HTMLElement>('#profiler-toggle');
  if (!toggle) {
    return;
  }

  toggle.style.position = 'absolute';
  toggle.style.height = 'auto';

  const scaleTransform = Number.isFinite(scale) && scale > 0 ? scale : 0.8;

  switch (position) {
    case 'top-left':
      toggle.style.top = `${y}px`;
      toggle.style.bottom = 'auto';
      toggle.style.left = `${x}px`;
      toggle.style.right = 'auto';
      toggle.style.transform = `scale(${scaleTransform})`;
      break;
    case 'top-right':
      toggle.style.top = `${y}px`;
      toggle.style.bottom = 'auto';
      toggle.style.right = `${x}px`;
      toggle.style.left = 'auto';
      toggle.style.transform = `scale(${scaleTransform})`;
      break;
    case 'bottom-left':
      toggle.style.bottom = `${y}px`;
      toggle.style.top = 'auto';
      toggle.style.left = `${x}px`;
      toggle.style.right = 'auto';
      toggle.style.transform = `scale(${scaleTransform})`;
      break;
    case 'bottom-right':
      toggle.style.bottom = `${y}px`;
      toggle.style.top = 'auto';
      toggle.style.right = `${x}px`;
      toggle.style.left = 'auto';
      toggle.style.transform = `scale(${scaleTransform})`;
      break;
    case 'center-top':
      toggle.style.top = `${y}px`;
      toggle.style.bottom = 'auto';
      toggle.style.left = `calc(50% + ${x}px)`;
      toggle.style.right = 'auto';
      toggle.style.transform = `translateX(-50%) scale(${scaleTransform})`;
      break;
    case 'center-bottom':
      toggle.style.bottom = `${y}px`;
      toggle.style.top = 'auto';
      toggle.style.left = `calc(50% + ${x}px)`;
      toggle.style.right = 'auto';
      toggle.style.transform = `translateX(-50%) scale(${scaleTransform})`;
      break;
  }
}
